import { Howl, Howler } from 'howler';

// ============================================================
// Audio system — Howler.js with real sound files
// ============================================================

export class AudioSystem {
  private engine: Howl;
  private engineId: number | null = null;
  private boost: Howl;
  private gunfire: Howl;
  private explosion: Howl;
  private missileLaunch: Howl;
  private missileWarning: Howl;
  private flareDeploy: Howl;
  private hitConfirm: Howl;
  private waterCrash: Howl;
  private music: Howl;

  private barrelRoll: Howl;
  private gunPlaying = false;
  private wasBoostingLastFrame = false;
  private warningPlaying = false;
  private warningId: number | null = null;
  private masterVol = 0.8;

  constructor() {
    this.engine = new Howl({
      src: ['/audio/engine-boost.mp3'],
      loop: true,
      volume: 0.3,
      preload: true,
    });

    this.boost = new Howl({
      src: ['/audio/engine-boost.mp3'],
      volume: 0.4,
      preload: true,
    });

    this.gunfire = new Howl({
      src: ['/audio/gunfire.mp3'],
      volume: 0.25,
      preload: true,
    });

    this.explosion = new Howl({
      src: ['/audio/explosion.mp3'],
      volume: 0.5,
      preload: true,
    });

    this.missileLaunch = new Howl({
      src: ['/audio/missile-launch.mp3'],
      volume: 0.4,
      preload: true,
    });

    this.missileWarning = new Howl({
      src: ['/audio/missile-warning.mp3'],
      loop: true,
      volume: 0.3,
      preload: true,
    });

    this.flareDeploy = new Howl({
      src: ['/audio/flare-deploy.mp3'],
      volume: 0.4,
      preload: true,
    });

    this.hitConfirm = new Howl({
      src: ['/audio/hit-confirm.mp3'],
      volume: 0.3,
      preload: true,
    });

    this.waterCrash = new Howl({
      src: ['/audio/water-crash.mp3'],
      volume: 0.5,
      preload: true,
    });

    this.barrelRoll = new Howl({
      src: ['/audio/barrel-roll.mp3'],
      volume: 0.3,
      preload: true,
    });

    this.music = new Howl({
      src: ['/audio/music-loop.mp3'],
      loop: true,
      volume: 0.6,
      preload: true,
    });
  }

  init(): void {
    const start = () => {
      Howler.ctx?.resume();
      this.engineId = this.engine.play();
      this.music.play();
      window.removeEventListener('click', start);
      window.removeEventListener('keydown', start);
    };
    window.addEventListener('click', start);
    window.addEventListener('keydown', start);
  }

  private vol(base: number): number {
    return base * this.masterVol;
  }

  updateEngine(speed: number, boosting: boolean): void {
    if (this.engineId === null) return;

    const rate = boosting ? 1.4 : 0.8 + speed * 0.004;
    this.engine.rate(Math.min(rate, 2), this.engineId);
    this.engine.volume(this.vol(boosting ? 0.5 : 0.3), this.engineId);

    this.wasBoostingLastFrame = boosting;
  }

  private gunId: number | null = null;

  updateGun(firing: boolean, _dt: number): void {
    if (firing && !this.gunPlaying) {
      this.gunfire.volume(this.vol(0.25));
      this.gunId = this.gunfire.play();
      this.gunPlaying = true;
      this.gunfire.once('end', () => { this.gunPlaying = false; this.gunId = null; });
    } else if (!firing && this.gunPlaying) {
      if (this.gunId !== null) this.gunfire.stop(this.gunId);
      this.gunPlaying = false;
      this.gunId = null;
    }
  }

  updateMissileWarning(incomingCount: number): void {
    if (incomingCount > 0 && !this.warningPlaying) {
      this.missileWarning.volume(this.vol(0.3));
      this.warningId = this.missileWarning.play();
      this.warningPlaying = true;
    } else if (incomingCount <= 0 && this.warningPlaying) {
      if (this.warningId !== null) this.missileWarning.stop(this.warningId);
      this.warningPlaying = false;
      this.warningId = null;
    }
  }

  playExplosion(): void {
    this.explosion.volume(this.vol(0.5));
    this.explosion.play();
  }

  playMissileLaunch(): void {
    this.missileLaunch.volume(this.vol(0.4));
    this.missileLaunch.play();
  }

  playFlare(): void {
    this.flareDeploy.volume(this.vol(0.4));
    this.flareDeploy.play();
    setTimeout(() => this.flareDeploy.play(), 150);
    setTimeout(() => this.flareDeploy.play(), 300);
  }

  playBarrelRoll(): void {
    this.barrelRoll.volume(this.vol(0.3));
    this.barrelRoll.play();
  }

  playHitConfirm(): void {
    this.hitConfirm.volume(this.vol(0.3));
    this.hitConfirm.play();
  }

  playWaterCrash(): void {
    this.waterCrash.volume(this.vol(0.5));
    this.waterCrash.play();
  }

  setMasterVolume(vol: number): void {
    this.masterVol = vol;
  }

  setMusicVolume(vol: number): void {
    this.music.volume(vol);
  }

  dispose(): void {
    this.engine.stop();
    this.missileWarning.stop();
    this.music.stop();
  }
}
